<template>
  <div class="tw-overflow-auto" @wheel="handleScroll">
    <FullCalendar ref="calendar" :options="calendarConfig" class="tw-min-w-[800px]">
      <template v-for="(_, slot) in $slots" #[slot]="attrs">
        <slot :name="slot" v-bind="attrs ?? {}" />
      </template>
    </FullCalendar>
  </div>
</template>

<script lang="ts">
export const SLOT_RESOLUTION_CHOICES = [1, 5, 10, 15, 30]
</script>

<script lang="ts" setup>
import { useI18n } from '@/i18n'
import { allowedEditPastProgram } from '@/permissions'
import { formatISOWeek, getClosestSlot, mapToDomain, secondsToTime } from '@/util'
import { CalendarOptions, DateSelectArg, EventApi, EventClickArg } from '@fullcalendar/core'
import fullCalendarInteractionPlugin from '@fullcalendar/interaction'
import fullCalendarTimeGridPlugin from '@fullcalendar/timegrid'
import FullCalendar from '@fullcalendar/vue3'
import { useKeyModifier, usePointer } from '@vueuse/core'
import { parseISO } from 'date-fns'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const week = defineModel<string>({ required: true })
const slotResolutionMinutes = defineModel<number>('slotResolutionMinutes', { required: true })
const props = defineProps<{
  events: CalendarOptions['events']
  start: Date | string
  end: Date | string
  config?: Partial<CalendarOptions>
}>()
const emit = defineEmits<{
  select: [DateSelectArg]
  selectDay: [Date]
  selectEvent: [EventClickArg]
  updateEvents: [EventApi[]]
}>()

const route = useRoute()
const { locale, t } = useI18n()

const calendar = ref<InstanceType<typeof FullCalendar>>()
const calendarAPI = computed(() => calendar.value?.getApi())
const currentCalendarResolutionMinutes = ref(slotResolutionMinutes.value)

const { pressure } = usePointer()
const isPointerDown = computed(() => pressure.value > 0)
const isAltDown = useKeyModifier('Alt')

async function zoom(updateSlotResolution: () => void, mouseY: number | null = null) {
  const calendarEl = calendar.value?.$el
  if (!calendarEl) return updateSlotResolution()
  const timeGridEl = calendarEl.querySelector('.fc-timegrid')
  const scrollerEl = calendarEl.querySelector('tbody .fc-scroller')
  const headerEl = calendarEl.querySelector('.fc-scrollgrid-section-sticky')
  if (!timeGridEl || !scrollerEl || !headerEl) return updateSlotResolution()

  const calendarRect = timeGridEl.getBoundingClientRect()
  const scrollBase =
    mouseY !== null
      ? mouseY - calendarRect.top + scrollerEl.scrollTop - headerEl.clientHeight
      : scrollerEl.scrollTop
  const scrollMinutes = mapToDomain(scrollBase, [0, scrollerEl.scrollHeight], [0, 24 * 60])
  updateSlotResolution()
  await nextTick()
  const newScrollBase = mapToDomain(scrollMinutes, [0, 24 * 60], [0, scrollerEl.scrollHeight])
  scrollerEl.scrollTop =
    mouseY === null
      ? newScrollBase
      : newScrollBase - (mouseY - headerEl.clientHeight - calendarRect.top)
}

async function setCalendarResolution(newResolution: number, mouseY: number | null = null) {
  await zoom(() => {
    slotResolutionMinutes.value = newResolution
    currentCalendarResolutionMinutes.value = newResolution
    calendarAPI.value?.setOption?.(
      'slotDuration',
      secondsToTime(newResolution * 60, { forceHours: true }),
    )
  }, mouseY)
}

function findCalenderResolution(indexOffset: number) {
  const presets = SLOT_RESOLUTION_CHOICES
  let currentIndex = presets.indexOf(slotResolutionMinutes.value)
  if (currentIndex === -1) {
    const closestPreset = presets.reduce((previousPreset, preset) => {
      return Math.abs(preset - slotResolutionMinutes.value) <
        Math.abs(previousPreset - slotResolutionMinutes.value)
        ? preset
        : previousPreset
    })
    currentIndex = presets.indexOf(closestPreset)
  }
  const newIndex = Math.max(0, Math.min(presets.length - 1, currentIndex + indexOffset))
  return presets[newIndex]
}

function handleScroll(event: WheelEvent) {
  if (!isAltDown.value) return

  event.preventDefault()
  const newResolution = findCalenderResolution(event.deltaY < 0 ? -1 : +1)
  if (newResolution !== slotResolutionMinutes.value) {
    // TODO: this task takes way to long for requestAnimationFrame
    requestAnimationFrame(() => {
      void setCalendarResolution(newResolution, event.clientY)
    })
  }
}

function maybeHighlight(el: HTMLElement | undefined | null, clear = false) {
  const selectionClass = 'is-selected'

  if (clear || el?.id) {
    for (const el of document.querySelectorAll('.calendar-event')) {
      el.classList.remove(selectionClass)
    }
  }

  if (el?.id) {
    el.classList.add(selectionClass)
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

watch(week, (newWeek) => {
  calendarAPI.value?.gotoDate?.(parseISO(newWeek))
})

watch(slotResolutionMinutes, (resolution) => {
  if (resolution !== currentCalendarResolutionMinutes.value) {
    setCalendarResolution(resolution)
  }
})

watch(
  () => route.hash,
  (newSelection) => {
    const id = newSelection.slice(1)
    if (id) {
      maybeHighlight(document.getElementById(id))
    }
  },
  { immediate: true },
)

const calendarConfig = computed<CalendarOptions>(() => ({
  plugins: [fullCalendarTimeGridPlugin, fullCalendarInteractionPlugin],
  initialView: 'timeGridWeek',
  locale: locale.value,
  initialDate: props.start,
  height: '100%',
  stickyHeaderDates: true,
  firstDay: 1,
  navLinks: true,
  events: props.events,
  buttonText: {
    today: t('calendar.today'),
  },
  headerToolbar: false,
  dayHeaderFormat: { day: 'numeric', month: 'numeric', weekday: 'short' },
  eventTimeFormat: { hour: 'numeric', minute: '2-digit' },
  slotLabelFormat: { hour: 'numeric', minute: '2-digit' },
  // This is set so that no event time in the title is displayed.
  // We still render event times in the actual event card.
  // See also the `end` generation in the ProgramWeekCalendarPage.vue component.
  displayEventTime: false,
  allDaySlot: false,
  editable: false,
  nowIndicator: true,
  eventDidMount({ el, event, timeText }) {
    const { durationMinutes } = event.extendedProps
    let title = event.title
    if (durationMinutes < slotResolutionMinutes.value) {
      title = `${timeText}: ${title}`
    }
    // here we add a simple tooltip to every event, so that the full title
    // of a show can be viewed
    el.setAttribute('title', title)

    if (event?.extendedProps?.domId) {
      el.setAttribute('id', event.extendedProps.domId)
      if (el.id === location.hash.slice(1)) {
        maybeHighlight(el)
      }
    }
  },
  datesSet: ({ start }) => {
    const weekStart = formatISOWeek(start)
    if (weekStart !== week.value) {
      week.value = weekStart
    }
  },
  eventClick(data) {
    maybeHighlight(null, true)
    emit('selectEvent', data)
  },
  select(data) {
    emit('select', data)
  },
  eventsSet(data) {
    emit('updateEvents', data)
  },
  selectMirror: true,
  slotDuration: secondsToTime(slotResolutionMinutes.value * 60, { forceHours: true }),
  // This value controls how events are rendered if the calendar resolution is larger
  // than the actual event duration.
  // Contrary to what the name of this property implies, height doesn’t seem to refer to a size,
  // neither relative nor absolute, but rather the duration of the event.
  // We choose 0 here so that events are never rendered side-by-side, which would likely be
  // interpreted as a conflict in the calendar.
  // See: https://gitlab.servus.at/aura/dashboard/-/issues/366#note_22295
  eventMinHeight: 0,
  selectAllow({ start }) {
    return allowedEditPastProgram.value || start >= getClosestSlot(slotResolutionMinutes.value)
  },
  selectOverlap: function (event) {
    if (event.extendedProps.disableSelection) return false
    return event.display === 'block' || event.display === 'background' || isPointerDown.value
  },
  navLinkDayClick(selectedDate) {
    emit('selectDay', selectedDate)
  },
  ...props.config,
}))

defineExpose({ calendarAPI })
</script>
